/* Copyright (c) 2018 Backpack Health LLC */
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ScrollAwareItem } from '../../components/ScrollAware';
import GroupTitle from '../../features/GroupTitle/GroupTitle.presenter';
import { IconClock, IconPlay } from '../../components/icons/SvgIcons';
import { GroupThemeMarkdown } from '../../components/Markdown';
import RouteNames from '../../appAuthorized/RouteNames';
import { SurveyPageLayout } from './SurveyQuestion/components/SurveyPageLayout';
import PreformattedText from '../../components/PreformattedText';
import { Card } from '../../../bphComponents/bundle';
import ContentWrapper from '../../components/ContentWrapper';
import { useUIState } from '../../hooks/useUIState';
import { useSurveyForActiveProfile } from '../../hooks/useSurvey';
import { useConsentForActiveProfile } from '../../hooks/useConsent';
import { useNavigate } from '../../hooks/useNavigate';
import AppUtils from '../../../utilities/AppUtils';

interface Props {
  match: any;
  location: any;
}

export const SurveyIntro: React.FC<Props> = (props) => {
  const groupId = Number(props.match.params.groupId);
  const surveyId = Number(props.match.params.surveyId);
  const searchParam = AppUtils.getQueryParams(props.location.search);
  const resume = Boolean(searchParam.resume);
  const [isReady, setIsReady] = React.useState(false);

  const [, uiActions] = useUIState();
  const [{ survey, definition }] = useSurveyForActiveProfile(surveyId);
  const [{ isInformedConsentForbidden }] = useConsentForActiveProfile();
  const [, navActions] = useNavigate();

  const start = (e) => {
    e.preventDefault();

    if (isInformedConsentForbidden) {
      uiActions.showErrorNotification('error.api-informed-consent.400-unavailable-language-error.message');
    } else {
      navActions.goTo(RouteNames.surveyFirstQuestion(groupId, surveyId));
    }
  };

  React.useEffect(() => {
    uiActions.setActiveGroup(groupId);
  }, []);

  React.useEffect(() => {
    if (!survey.id) { return; }
    if (resume && survey.completion_percentage > 0) {
      navActions.replace(RouteNames.surveyFirstQuestion(groupId, surveyId));
    }
    setIsReady(true);
  }, [survey.id]);

  if (!survey || !definition || !isReady) return null;

  return (
    <SurveyPageLayout
      className=""
      navButtons={{
        cancel: () => navActions.goBackTo(RouteNames.surveys(groupId)),
        next: start,
        nextProps: {
          theme: 'group',
        },
      }}
      groupId={groupId}
      progress={
        <ScrollAwareItem>{({ displayTitle }) => !!displayTitle &&
          <div
            className="font-caption-1 theme-group base color"
            style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {definition.name}
          </div>}
        </ScrollAwareItem>}
      mainTitle={
        <div>
          <div className="font-caption-1 theme-group base color">
            <GroupTitle
              groupId={groupId}
              size="48"
            />
          </div>

          <div className="font-title-2 theme-group base color" style={{ paddingTop: 8 }}>
            {definition.name}
          </div>
        </div>
      }
      extendedHeader={() => (
        <div className="theme-group base color">
          <PreformattedText className="font-body">
            {definition.short_description}
          </PreformattedText>

          <div className="font-subhead" style={{ marginTop: 18, display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: 14 }}>
              <IconClock strokeClassName="theme-group base stroke"/>
            </div>

            <div>
              <FormattedMessage id="ui-data.surveys.estimatedTime.text" values={{ string1: definition.estimated_completion_mins }}/>
            </div>
          </div>
        </div>
      )}
    >
      <ContentWrapper>
        <Card.CardShape>
          <Card.CardContent>
            <GroupThemeMarkdown text={definition.intro_message} />
          </Card.CardContent>
        </Card.CardShape>

        <Card.CardShape>
          <Card.CardContent onClick={start}>
            <div>
              <a style={{ display: 'flex', alignItems: 'center' }} href="#">
                <div className="font-title-2 theme-group base color" style={{ marginRight: 16 }}>
                  <FormattedMessage id="surveyIntro.getStarted.button.label"/>
                </div>

                <IconPlay
                  fillClassName="theme-group base fill"
                  borderClassName="theme-group base border"
                />
              </a>

              <div className="font-subhead font-color-gsMedDark" style={{ marginTop: 12 }}>
                <FormattedMessage id="surveyIntro.getStarted.message"/>
              </div>
            </div>
          </Card.CardContent>
        </Card.CardShape>
      </ContentWrapper>
    </SurveyPageLayout>
  );
};
